import type { CheckVerdict } from '../../../shared/protocol.ts';
import type { TailClip } from './recorder.ts';

/**
 * Loads a clip into a media element. Fragmented MP4 goes through Media Source Extensions,
 * which plays fragments wherever they sit in time and proves the bytes decode.
 * Returns the media time at which the clip's content begins.
 */
export async function mountClip(clip: TailClip, el: HTMLMediaElement): Promise<number> {
  const urlOf = (b: Blob) => URL.createObjectURL(b);
  const blob = new Blob([clip.data as Uint8Array<ArrayBuffer>], { type: clip.mime.split(';')[0] });
  const MS: typeof MediaSource | undefined = (window as any).ManagedMediaSource ?? window.MediaSource;
  if (clip.audioOnly || !MS || !MS.isTypeSupported(clip.mime)) {
    el.src = urlOf(blob);
    await waitFor(el, 'loadedmetadata');
    return 0;
  }
  const ms = new MS();
  (el as HTMLVideoElement).disableRemotePlayback = true;
  el.src = URL.createObjectURL(ms);
  await waitFor(ms, 'sourceopen');
  const sb = ms.addSourceBuffer(clip.mime);
  sb.appendBuffer(clip.data as Uint8Array<ArrayBuffer>);
  await waitFor(sb, 'updateend');
  const start = sb.buffered.length ? sb.buffered.start(0) : 0;
  try {
    ms.endOfStream();
  } catch {}
  if (el.readyState < 1) await waitFor(el, 'loadedmetadata');
  return start;
}

function waitFor(target: EventTarget, event: string, timeoutMs = 8000) {
  return new Promise<void>((resolve, reject) => {
    const t = setTimeout(() => reject(new Error(`Timed out waiting for ${event}`)), timeoutMs);
    target.addEventListener(
      event,
      () => {
        clearTimeout(t);
        resolve();
      },
      { once: true },
    );
    target.addEventListener('error', () => {
      clearTimeout(t);
      reject(new Error('This browser could not decode the recording'));
    }, { once: true });
  });
}

export function audioVerdict(clip: TailClip): CheckVerdict {
  const peak = clip.peakDb ?? -Infinity;
  const rms = clip.rmsDb ?? -Infinity;
  const base = { peakDb: round1(peak), rmsDb: round1(rms), seconds: round1(clip.seconds) };
  if (clip.hardMuted) return { ...base, ok: true, summary: 'Hard-muted – silence is being recorded, as chosen' };
  if (!Number.isFinite(peak) || peak < -60) return { ...base, ok: false, summary: 'Silent – check the microphone is selected and unmuted in your system settings' };
  if (peak < -40) return { ...base, ok: false, summary: `Very quiet (peak ${round1(peak)} dB) – move closer or raise input gain` };
  if (peak > -0.3) return { ...base, ok: false, summary: 'Clipping – lower the input gain to avoid distortion' };
  return { ...base, ok: true, summary: `Sound OK · peak ${round1(peak)} dB` };
}

/** Samples a few frames from a playing video to catch black or frozen pictures. */
export async function videoVerdict(clip: TailClip, el: HTMLVideoElement, expectedFps?: number): Promise<CheckVerdict> {
  const canvas = document.createElement('canvas');
  canvas.width = 48;
  canvas.height = 27;
  const ctx = canvas.getContext('2d', { willReadFrequently: true })!;
  const frames: Uint8ClampedArray[] = [];
  // Give playback a moment to start, then sample across the clip.
  await new Promise((r) => setTimeout(r, 400));
  for (let i = 0; i < 4; i++) {
    await new Promise((r) => setTimeout(r, Math.max(150, (clip.seconds * 1000) / 6)));
    if (!el.videoWidth) continue;
    ctx.drawImage(el, 0, 0, canvas.width, canvas.height);
    frames.push(ctx.getImageData(0, 0, canvas.width, canvas.height).data.slice());
  }
  const luma = frames.length
    ? frames.reduce((acc, f) => {
        let sum = 0;
        for (let i = 0; i < f.length; i += 4) sum += 0.2126 * f[i] + 0.7152 * f[i + 1] + 0.0722 * f[i + 2];
        return acc + sum / (f.length / 4);
      }, 0) / frames.length
    : 0;
  let changed = false;
  for (let i = 1; i < frames.length && !changed; i++) {
    for (let p = 0; p < frames[i].length; p += 4) {
      if (Math.abs(frames[i][p] - frames[0][p]) > 2) {
        changed = true;
        break;
      }
    }
  }
  const base: CheckVerdict = {
    ok: true,
    summary: '',
    width: el.videoWidth,
    height: el.videoHeight,
    frameRate: clip.frameRate,
    lumaMean: round1(luma),
    seconds: round1(clip.seconds),
  };
  const res = `${el.videoWidth}×${el.videoHeight} · ${clip.frameRate ?? '?'} fps`;
  if (!el.videoWidth) return { ...base, ok: false, summary: 'No picture decoded' };
  if (clip.hardMuted) return { ...base, ok: true, summary: `Hard-muted – black is being recorded, as chosen (${res})` };
  if (luma < 6) return { ...base, ok: false, summary: `Picture is black (${res}) – lens cap, privacy shutter or wrong camera?` };
  if (expectedFps && clip.frameRate != null && clip.frameRate < expectedFps * 0.6)
    return { ...base, ok: false, summary: `Low frame rate (${res}) – this machine may be overloaded` };
  if (!changed && clip.kind === 'camera' && frames.length > 2)
    return { ...base, ok: false, summary: `Picture looks frozen (${res})` };
  return { ...base, ok: true, summary: `Picture OK · ${res}` };
}

const round1 = (x: number) => (Number.isFinite(x) ? Math.round(x * 10) / 10 : x);
